import { Injectable, NgZone } from '@angular/core';
import apiConfig from './api-client/api-config';
import { ApiRequestService } from './api-client/api-requests.service';
import { Constants } from './utils/Constants';
import { environment } from 'src/environments/environment';
import { TokenService } from './token.service';

@Injectable()
export class DrAuthService {
  private refreshTimeout: any;
  private baseKey = `CognitoIdentityServiceProvider.${environment.clientId}`;

  constructor(
    private apiRequest: ApiRequestService,
    private ngZone: NgZone,
    private tokenService: TokenService
  ) {}

  getEmail(): string | null {
    return localStorage.getItem(`${this.baseKey}.LastAuthUser`);
  }

  getPrimaryKey(email: string): string {
    return `${this.baseKey}.${email}`;
  }

  getSecondaryKey(email: string): string {
    return `CognitoIdentityServiceProvider.${environment.clientIdSecondary}.${email}.secondary`;
  }

  getToken(key: string, suffix: string): string | null {
    return localStorage.getItem(`${key}.${suffix}`);
  }

  get expireAt(): number {
    const email = this.getEmail();
    if (!email) return 0;

    const key = this.getPrimaryKey(email);
    const expiresAt = this.getToken(key, 'expiresat');
    return expiresAt ? +expiresAt - 60 : 0;
  }

  shouldRefresh(): boolean {
    const exp = this.expireAt;
    return exp !== 0 && exp < Date.now() / 1000;
  }

  refreshToken(): void {
    const email = this.getEmail();
    if (!email) return;

    const primaryKey = this.getPrimaryKey(email);
    const secondaryKey = this.getSecondaryKey(email);

    const primaryToken = this.getToken(primaryKey, 'refreshToken');
    const secondaryToken = this.getToken(secondaryKey, 'refreshToken');

    if (!primaryToken && !secondaryToken) return;

    const { region, regionSecondary } = environment;
    const body = {
      refreshtoken: primaryToken,
      refreshtokensecondary: secondaryToken,
    };

    this.apiRequest.callApi(Constants.API_METHODS.POST, apiConfig.refreshToken.uri, body, false)
      .subscribe({
        next: (res) => {
          this.tokenService.setTokens(res[region], res[regionSecondary], email);
          this.init();
        },
        error: console.error
      });
  }

  scheduleRefresh(): void {
    const delay = (this.expireAt - Date.now() / 1000) * 1000;
    if (delay <= 0) return;

    this.clearTimer();

    console.log(`Scheduling token refresh in ${delay}ms`);

    this.ngZone.runOutsideAngular(() => {
      this.refreshTimeout = setTimeout(() => {
        this.ngZone.run(() => this.refreshToken());
      }, delay);
    });
  }

  clearTimer(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
  }

  async init(): Promise<void> {
    if (this.shouldRefresh()) {
      await this.refreshToken();
    }
    this.scheduleRefresh();
    this.handshake();
  }

  handshake(): void {
    const email = this.getEmail();
    if (!email) return;

    const primaryKey = this.getPrimaryKey(email);
    const secondaryKey = this.getSecondaryKey(email);

    const primaryToken = this.getToken(primaryKey, 'idToken');
    const secondaryToken = this.getToken(secondaryKey, 'idToken');

    if (!!primaryToken !== !!secondaryToken) {
      const { region, regionSecondary } = environment;

      this.apiRequest.callApi(Constants.API_METHODS.GET, apiConfig.handshake.uri, null, false)
        .subscribe({
          next: (res) => {
            this.tokenService.setTokens(res[region], res[regionSecondary], email);
          },
          error: console.error
        });
    }
  }
}
